import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Mail, Calculator } from "lucide-react";
import { supabase } from "@/lib/supabase";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Separator } from "./ui/separator";

const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(6, "Phone number must be at least 6 digits"),
  message: z.string().min(10, "Message must be at least 10 characters"),
  package_name: z.string(),
});

export type EnhancedQuoteFormValues = z.infer<typeof formSchema>;

interface QuoteDetails {
  persons: number;
  nights: number;
  extraPersons: number;
  selectedRoom: any;
  selectedAddons: any[];
  breakdown: {
    basePrice: number;
    personCost: number;
    nightCost: number;
    roomCost: number;
    addonsCost: number;
  };
}

interface EnhancedQuoteFormProps {
  packageName: string;
  packageId: number;
  totalPrice: number;
  quoteDetails: QuoteDetails;
  onClose?: () => void;
}

export function EnhancedQuoteForm({ 
  packageName, 
  packageId, 
  totalPrice, 
  quoteDetails, 
  onClose 
}: EnhancedQuoteFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  
  const form = useForm<EnhancedQuoteFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: `I'm interested in the ${packageName} package with the customizations shown.`,
      package_name: packageName,
    },
  });

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const newImages: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;

        const { data, error: uploadError } = await supabase.storage
          .from('quote-images')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('quote-images')
          .getPublicUrl(fileName);

        newImages.push(publicUrl);
      }

      setUploadedImages([...uploadedImages, ...newImages]);
      toast.success("Images uploaded successfully!");
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (values: EnhancedQuoteFormValues) => {
    console.log("Starting enhanced quote submission...");
    setSubmitting(true);
    
    try {
      // Insert quote request
      const { data: quoteData, error: quoteError } = await supabase
        .from("quote_requests")
        .insert([{
          name: values.name,
          email: values.email,
          phone: values.phone,
          message: values.message,
          package_name: packageName,
          package_id: packageId,
          total_price: totalPrice,
          persons: quoteDetails.persons,
          nights: quoteDetails.nights,
          extra_persons: quoteDetails.extraPersons,
          selected_room: quoteDetails.selectedRoom,
          selected_addons: quoteDetails.selectedAddons,
          price_breakdown: quoteDetails.breakdown,
          images: uploadedImages,
        }])
        .select()
        .single();

      if (quoteError) throw quoteError;

      // Send email notification using edge function
      const { error: emailError } = await supabase.functions.invoke('send-quote-email', {
        body: {
          quoteData,
          customerData: values,
          packageDetails: {
            name: packageName,
            totalPrice,
            details: quoteDetails
          }
        }
      });

      if (emailError) {
        console.error("Email error:", emailError);
        // Don't fail the whole process if email fails
        toast.warning("Quote submitted but email notification failed");
      }

      console.log("Enhanced quote submitted successfully");
      form.reset();
      setShowSuccessDialog(true);
      setUploadedImages([]);
      
      toast.success("Quote request submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting enhanced quote request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    onClose?.();
  };

  return (
    <>
      <Dialog open={showSuccessDialog} onOpenChange={handleSuccessClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center mb-4 flex items-center justify-center gap-2">
              <Mail className="w-6 h-6 text-shell-primary" />
              Quote Request Submitted!
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <p className="text-lg">Thank you for your interest in our {packageName} package!</p>
            <p className="mt-2 text-muted-foreground">
              We have received your detailed quote request for <strong>${totalPrice.toFixed(2)}</strong> and will contact you shortly.
            </p>
            <p className="mt-2 text-sm text-shell-primary">
              A confirmation email has been sent to your email address.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={handleSuccessClose} className="w-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        {/* Price Summary Card */}
        <Card className="border-shell-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-shell-primary" />
              Your Quote Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Package:</p>
                <p className="font-medium">{packageName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Duration:</p>
                <p className="font-medium">{quoteDetails.persons} persons × {quoteDetails.nights} nights</p>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Base Price:</span>
                <span>${quoteDetails.breakdown.basePrice.toFixed(2)}</span>
              </div>
              {quoteDetails.breakdown.personCost > 0 && (
                <div className="flex justify-between">
                  <span>Person Cost:</span>
                  <span>${quoteDetails.breakdown.personCost.toFixed(2)}</span>
                </div>
              )}
              {quoteDetails.breakdown.nightCost > 0 && (
                <div className="flex justify-between">
                  <span>Night Cost:</span>
                  <span>${quoteDetails.breakdown.nightCost.toFixed(2)}</span>
                </div>
              )}
              {quoteDetails.breakdown.roomCost > 0 && (
                <div className="flex justify-between">
                  <span>Room Upgrade:</span>
                  <span>${quoteDetails.breakdown.roomCost.toFixed(2)}</span>
                </div>
              )}
              {quoteDetails.breakdown.addonsCost > 0 && (
                <div className="flex justify-between">
                  <span>Add-ons:</span>
                  <span>${quoteDetails.breakdown.addonsCost.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            <Separator />
            
            <div className="flex justify-between text-lg font-bold text-shell-primary">
              <span>Total Price:</span>
              <span>${totalPrice.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Contact Form */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your name" {...field} disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="your@email.com" type="email" {...field} disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Your phone number" type="tel" {...field} disabled={submitting} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Your message" 
                      {...field} 
                      disabled={submitting}
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('image-upload')?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Images
                    </>
                  )}
                </Button>
                <input
                  id="image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </div>

              {uploadedImages.length > 0 && (
                <Carousel className="w-full max-w-xs mx-auto">
                  <CarouselContent>
                    {uploadedImages.map((url, index) => (
                      <CarouselItem key={index}>
                        <div className="p-1">
                          <div className="relative aspect-square">
                            <img
                              src={url}
                              alt={`Uploaded image ${index + 1}`}
                              className="object-cover rounded-lg w-full h-full"
                            />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting Quote...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Submit Quote Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </div>
    </>
  );
}